import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DateInput } from "../../components/ui/DateInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { BillingSidebar } from "../../components/billing/BillingSidebar";
import { searchPatients, getPatientById, getPatientByDisplayId } from "../../services/patient.service";
import {
  createInvoice,
  getInvoice,
  updateInvoice,
  sendInvoice,
  getPatientInvoices,
  InvoiceApiError,
} from "../../services/invoice.service";
import { DiagnosisAPI } from "../../services/diagnosis.service";
import { DoctorAPI, type DoctorListItem } from "../../services/doctor.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import type { BillableVisit } from "../../services/diagnosis.service";
import { GST_SLABS, type GstSlab, type InvoiceResponse, type InvoiceUpdate } from "../../schemas/invoice.schema";
import type { BillItem, BillItemCategory } from "./types";


// ── Helpers ────────────────────────────────────────────────────────────────────

const formatINR = (paise: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(paise / 100);

const todayISO = () => new Date().toISOString().slice(0, 10);

const formatDateDisplay = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric", weekday: "short" });
};

const formatVisitLabel = (v: BillableVisit, docName?: string) => {
  const date = new Date(v.visit_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const doc = docName || v.doctor_name;
  const docLabel = doc ? ` · ${doc.startsWith("Dr.") ? doc : `Dr. ${doc}`}` : "";
  const complaint = v.chief_complaint ? ` · ${v.chief_complaint}` : "";
  return `${date} · ${v.status}${docLabel}${complaint}`;
};

export const detectItemCategory = (desc: string): BillItemCategory => {
  const d = desc.toLowerCase().trim();
  if (
    d.includes("consultation") ||
    d.includes("consult") ||
    d.includes("doctor fee") ||
    d.includes("visit fee") ||
    d.includes("follow up") ||
    d.includes("follow-up")
  ) {
    return "consultation";
  }
  if (
    d.includes("tablet") ||
    d.includes("tab") ||
    d.includes("capsule") ||
    d.includes("cap") ||
    d.includes("syrup") ||
    d.includes("suspension") ||
    d.includes("injection") ||
    d.includes("inj") ||
    d.includes("drops") ||
    d.includes("ointment") ||
    d.includes("cream") ||
    d.includes("gel") ||
    d.includes("lotion") ||
    d.includes("powder") ||
    d.includes("pharma") ||
    d.includes("medication") ||
    d.includes("medicine") ||
    d.includes("paracetamol") ||
    d.includes("amoxicillin") ||
    /\b\d+\s*(mg|mcg|ml|g)\b/i.test(d)
  ) {
    return "medication";
  }
  return "procedure";
};

const newItem = (category?: BillItemCategory): BillItem => ({
  id: crypto.randomUUID(),
  description: "",
  category: category ?? "procedure",
  categoryManuallySet: false,
  quantity: 1,
  rate: 0,
  discount: 0,
  tax: 0,
});


// ── Patient search dropdown ────────────────────────────────────────────────────

interface PatientDropdownProps {
  query: string;
  onQueryChange: (q: string) => void;
  onSelect: (p: PatientDbRecord) => void;
}

const PatientSearchDropdown: React.FC<PatientDropdownProps> = ({ query, onQueryChange, onSelect }) => {
  const [results, setResults] = useState<PatientDbRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const results = await searchPatients(q.trim(), 8);
      setResults(results);
      setOpen(results.length > 0);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (q: string) => {
    onQueryChange(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(q), 250);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Search by name, phone, or patient ID..."
          className="w-full pl-9 pr-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 placeholder-slate-400"
        />
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-[#00b8db]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
          </span>
        )}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-auto max-h-48 text-sm">
          {results.map((p) => (
            <li
              key={p.patient_id}
              className="px-4 py-2.5 hover:bg-[#f0fdfe] cursor-pointer flex items-center justify-between"
              onMouseDown={() => { onSelect(p); setOpen(false); }}
            >
              <div>
                <p className="font-medium text-slate-800">{p.name || `${p.first_name} ${p.last_name}`}</p>
                <p className="text-slate-400 text-xs">{p.display_id} · {p.contact_number}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Read-only input ────────────────────────────────────────────────────────────

const ROInput: React.FC<{ value: string; placeholder?: string }> = ({ value, placeholder }) => (
  <input
    readOnly
    value={value}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg opacity-50 cursor-not-allowed focus:outline-none"
  />
);

// ── Page ───────────────────────────────────────────────────────────────────────

const CreateBillPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Patient / visit selection ────────────────────────────────────────────────
  const [patientQuery, setPatientQuery] = useState("");
  const [patient, setPatient] = useState<PatientDbRecord | null>(null);
  const [visits, setVisits] = useState<BillableVisit[]>([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<BillableVisit | null>(null);
  const [doctors, setDoctors] = useState<DoctorListItem[]>([]);

  useEffect(() => {
    DoctorAPI.list({ is_active: true })
      .then(setDoctors)
      .catch(() => DoctorAPI.getAll().then(setDoctors).catch(() => {}));
  }, []);

  const currentDoctor = useMemo(() => {
    if (!selectedVisit) return null;
    return doctors.find((d) => d.doctor_id === selectedVisit.doctor_id) ?? null;
  }, [selectedVisit, doctors]);

  const doctorName = useMemo(() => {
    if (currentDoctor?.name) {
      return currentDoctor.name.startsWith("Dr.") ? currentDoctor.name : `Dr. ${currentDoctor.name}`;
    }
    if (selectedVisit?.doctor_name) {
      return selectedVisit.doctor_name.startsWith("Dr.") ? selectedVisit.doctor_name : `Dr. ${selectedVisit.doctor_name}`;
    }
    return "";
  }, [currentDoctor, selectedVisit]);

  const doctorConsultationFee = useMemo(() => {
    if (currentDoctor?.consultation_fee != null && currentDoctor.consultation_fee > 0) {
      return currentDoctor.consultation_fee;
    }
    if (selectedVisit?.consultation_fee_paise != null && selectedVisit.consultation_fee_paise > 0) {
      return selectedVisit.consultation_fee_paise / 100;
    }
    return 0;
  }, [currentDoctor, selectedVisit]);

  // ── Bill form ────────────────────────────────────────────────────────────────
  const [billDate, setBillDate] = useState(todayISO());
  const [items, setItems] = useState<BillItem[]>([newItem()]);
  const [notes, setNotes] = useState("");
  const [discountPct, setDiscountPct] = useState(0);

  // ── Backend invoice ──────────────────────────────────────────────────────────
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [serverSummary, setServerSummary] = useState<InvoiceResponse | null>(null);
  const [isPatching, setIsPatching] = useState(false);
  // Autosave and GST saves can overlap. Each save takes a number, and only the
  // latest one may write the summary, so a slower earlier response can never
  // show a stale GST rate or total.
  const patchSeqRef = useRef(0);
  const updateSummary = useCallback(async (id: string, data: InvoiceUpdate) => {
    const seq = ++patchSeqRef.current;
    setIsPatching(true);
    try {
      const inv = await updateInvoice(id, data);
      if (seq === patchSeqRef.current) setServerSummary(inv);
    } finally {
      if (seq === patchSeqRef.current) setIsPatching(false);
    }
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── URL pre-population (patient profile → "Create Bill" or "Edit Draft") ────
  const urlPatientId = searchParams.get("patient_id") || searchParams.get("patientId");
  const urlVisitId = searchParams.get("visit_id") || searchParams.get("visitId");
  const urlInvoiceId = searchParams.get("invoiceId");
  const preloaded = useRef(false);
  // Set to true before calling setSelectedVisit when loading an existing draft,
  // so the visit-selection effect skips creating a new invoice.
  const skipInvoiceCreate = useRef(false);

  useEffect(() => {
    if (!urlPatientId || preloaded.current) return;
    preloaded.current = true;
    (async () => {
      try {
        const isDisplayId = urlPatientId.toUpperCase().startsWith("PAT-");
        const found = isDisplayId
          ? await getPatientByDisplayId(urlPatientId)
          : await getPatientById(urlPatientId);
        if (!found) return;
        setPatient(found);
        setPatientQuery(found.name || `${found.first_name} ${found.last_name}`);
        setVisitsLoading(true);
        const [vs, invoices] = await Promise.all([
          DiagnosisAPI.getBillableVisits(found.patient_id!),
          getPatientInvoices(found.patient_id!).catch(() => []),
        ]);
        // Visits that already have a non-draft invoice are excluded
        const billedVisitIds = new Set(
          invoices.filter((inv) => inv.status !== "draft").map((inv) => inv.visit_id)
        );
        const sorted = vs
          .filter((v) => v.status.toLowerCase() === "finalized" && !billedVisitIds.has(v.visit_id))
          .sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime());
        setVisits(sorted);

        if (urlVisitId) {
          const match = sorted.find((v) => v.visit_id === urlVisitId);
          if (match) {
            if (urlInvoiceId) {
              // Load existing draft — populate form from server, skip create
              const inv = await getInvoice(urlInvoiceId);
              if (inv.status !== "draft") {
                toast.error("Only draft invoices can be edited.");
                return;
              }
              skipInvoiceCreate.current = true;
              setSelectedVisit(match);
              setInvoiceId(inv.invoice_id);
              setServerSummary(inv);
              if (inv.notes) setNotes(inv.notes);
              if (inv.bill_date) setBillDate(inv.bill_date);
              if (inv.line_items && inv.line_items.length > 0) {
                setItems(inv.line_items.map((li) => {
                  const isConsultation = li.description.toLowerCase().includes("consultation");
                  const rateVal = li.rate_paise === 0 && isConsultation && doctorConsultationFee > 0
                    ? doctorConsultationFee
                    : li.rate_paise / 100;
                  return {
                    ...newItem(),
                    description: li.description,
                    quantity: li.qty,
                    rate: rateVal,
                  };
                }));
              } else {
                const defaultRate = inv.consultation_fee_paise > 0 ? inv.consultation_fee_paise / 100 : doctorConsultationFee;
                setItems([{ ...newItem(), description: "Consultation Fee", rate: defaultRate }]);
              }
            } else {
              setSelectedVisit(match);
            }
          }
        }
      } catch {
        // preload failures are silent — user can still pick manually
      } finally {
        setVisitsLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPatientId, urlVisitId, urlInvoiceId]);

  // ── Visit selected: reset form, create or load draft invoice ─────────────────
  useEffect(() => {
    if (!selectedVisit) return;
    // Skip when loading an existing draft (URL has invoiceId) or already loaded
    if (skipInvoiceCreate.current) {
      skipInvoiceCreate.current = false;
      return;
    }
    // If invoiceId is already set (loaded from URL), don't create a duplicate
    if (invoiceId && urlInvoiceId) return;
    let active = true;

    const fee = doctorConsultationFee;
    setItems([{ ...newItem(), description: "Consultation Fee", rate: fee }]);
    setInvoiceId(null);
    setServerSummary(null);

    // Check if an existing draft already exists for this visit before creating a new one
    getPatientInvoices(selectedVisit.patient_id)
      .catch(() => [])
      .then((invList) => {
        if (!active) return;
        const existingDraft = invList.find(
          (inv) => inv.visit_id === selectedVisit.visit_id && inv.status === "draft"
        );
        if (existingDraft) {
          setInvoiceId(existingDraft.invoice_id);
          setServerSummary(existingDraft);
          if (existingDraft.notes) setNotes(existingDraft.notes);
          if (existingDraft.bill_date) setBillDate(existingDraft.bill_date);
          if (existingDraft.line_items && existingDraft.line_items.length > 0) {
            setItems(
              existingDraft.line_items.map((li) => {
                const isConsultation = li.description.toLowerCase().includes("consultation");
                const rateVal = li.rate_paise === 0 && isConsultation && fee > 0
                  ? fee
                  : li.rate_paise / 100;
                const cat = li.category || detectItemCategory(li.description);
                return {
                  ...newItem(cat),
                  description: li.description,
                  category: cat,
                  categoryManuallySet: Boolean(li.category),
                  quantity: li.qty,
                  rate: rateVal,
                  discount: li.discount_paise > 0 && li.rate_paise > 0
                    ? Math.round((li.discount_paise / (li.qty * li.rate_paise)) * 100)
                    : 0,
                };
              })
            );
          } else {
            const draftFee = existingDraft.consultation_fee_paise > 0 ? existingDraft.consultation_fee_paise / 100 : fee;
            setItems([{ ...newItem("consultation"), description: "Consultation Fee", rate: draftFee, category: "consultation", categoryManuallySet: true }]);
          }
          return;
        }

        return createInvoice(selectedVisit.visit_id, {
          consultation_fee_paise: Math.round(fee * 100),
          medication_charges_paise: 0,
          procedure_charges_paise: 0,
          discount_paise: 0,
          // gst_rate omitted: the server applies the clinic default slab.
        }).then((inv) => {
          if (!active) return;
          setInvoiceId(inv.invoice_id);
          setServerSummary(inv);
        });
      })
      .catch((err) => {
        if (active) toast.error(err instanceof InvoiceApiError ? err.message : "Could not initialize invoice draft.");
      });

    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVisit]);

  // Update initial Consultation Fee rate if doctors loaded after visit was selected
  useEffect(() => {
    if (!selectedVisit || doctorConsultationFee <= 0) return;
    setItems((prev) => {
      if (prev.length === 1 && prev[0].description === "Consultation Fee" && prev[0].rate === 0) {
        return [{ ...prev[0], rate: doctorConsultationFee }];
      }
      return prev;
    });
  }, [doctorConsultationFee, selectedVisit]);

  // ── Derived totals ───────────────────────────────────────────────────────────
  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.quantity * it.rate, 0),
    [items],
  );
  // Sum of all per-line-item discounts (each item.discount is a %)
  const itemDiscountTotal = useMemo(
    () => items.reduce((s, it) => s + it.quantity * it.rate * (it.discount / 100), 0),
    [items],
  );
  // Total discount = per-item discounts + global % (applied on the net-of-item-discounts amount)
  const discountAmt = useMemo(() => {
    const netAfterItemDiscounts = subtotal - itemDiscountTotal;
    const globalDiscount = netAfterItemDiscounts * (discountPct / 100);
    return itemDiscountTotal + globalDiscount;
  }, [subtotal, itemDiscountTotal, discountPct]);

  // ── Debounced PATCH: sync form → backend 500ms after edits ──────────────────
  useEffect(() => {
    if (!invoiceId) return;
    const timer = setTimeout(async () => {
      try {
        let consultationPaise = 0;
        let medicationPaise = 0;
        let procedurePaise = 0;

        items.forEach((it) => {
          if (!it.description.trim()) return;
          const gross = Math.round(it.quantity * it.rate * 100);
          const cat = it.category || detectItemCategory(it.description);
          if (cat === "consultation") consultationPaise += gross;
          else if (cat === "medication") medicationPaise += gross;
          else procedurePaise += gross;
        });

        await updateSummary(invoiceId, {
          consultation_fee_paise: consultationPaise,
          medication_charges_paise: medicationPaise,
          procedure_charges_paise: procedurePaise,
          discount_paise: Math.round(discountAmt * 100),
          // gst_rate omitted: it changes only through handleGstChange, so a
          // late debounced save can never revert a rate the user just picked.
          notes: notes.trim() || undefined,
          bill_date: billDate || undefined,
          line_items: items
            .filter((it) => it.description.trim())
            .map((it) => ({
              description: it.description,
              category: it.category || detectItemCategory(it.description),
              qty: it.quantity,
              rate_paise: Math.round(it.rate * 100),
              discount_paise: Math.round(it.rate * it.quantity * it.discount),
            })),
        });
      } catch {
        // silent — don't interrupt the user while they're editing
      }
    }, 500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, discountPct, notes, billDate, invoiceId]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleSelectPatient = async (p: PatientDbRecord) => {
    setPatient(p);
    setPatientQuery(p.name || `${p.first_name} ${p.last_name}`);
    setSelectedVisit(null);
    setVisits([]);
    setInvoiceId(null);
    setServerSummary(null);
    if (!p.patient_id) return;
    setVisitsLoading(true);
    try {
      const [vs, invoices] = await Promise.all([
        DiagnosisAPI.getBillableVisits(p.patient_id),
        getPatientInvoices(p.patient_id).catch(() => []),
      ]);
      const billedVisitIds = new Set(
        invoices.filter((inv) => inv.status !== "draft").map((inv) => inv.visit_id)
      );
      setVisits(
        vs.filter((v) => v.status.toLowerCase() === "finalized" && !billedVisitIds.has(v.visit_id))
          .sort((a, b) => new Date(b.visit_date).getTime() - new Date(a.visit_date).getTime())
      );
    } catch {
      toast.error("Could not load visits for this patient.");
    } finally {
      setVisitsLoading(false);
    }
  };

  const updateItem = (id: string, field: keyof BillItem, raw: string | number) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== id) return it;
      if (field === "category") {
        return { ...it, category: raw as BillItemCategory, categoryManuallySet: true };
      }
      if (field === "description") {
        const desc = String(raw);
        const updatedCat = it.categoryManuallySet ? it.category : detectItemCategory(desc);
        return { ...it, description: desc, category: updatedCat };
      }
      const n = Number(raw);
      const v =
        field === "quantity" ? Math.max(1, n || 1) :
        field === "rate"     ? Math.max(0, n || 0) :
        field === "discount" ? Math.min(100, Math.max(0, n || 0)) :
        field === "tax"      ? Math.min(100, Math.max(0, n || 0)) :
        raw;
      return { ...it, [field]: v };
    }));
  };

  // GST is chosen per invoice and saved at once; the server computes the
  // amount, and refuses GST when the clinic has no GSTIN on file.
  const handleGstChange = async (rate: GstSlab) => {
    if (!invoiceId) return;
    try {
      await updateSummary(invoiceId, { gst_rate: rate });
    } catch (err) {
      toast.error(err instanceof InvoiceApiError ? err.message : "Could not update GST.");
    }
  };

  const removeItem = (id: string) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const handleGenerateBill = async () => {
    if (!patient)       { toast.error("Please select a patient first."); return; }
    if (!selectedVisit) { toast.error("Please select a visit."); return; }
    if (!invoiceId)     { toast.error("Invoice not initialized — please wait."); return; }
    if (items.every((it) => !it.description.trim())) {
      toast.error("Add at least one bill item.");
      return;
    }
    setIsSubmitting(true);
    try {
      const sent = await sendInvoice(invoiceId);
      sent.warnings?.forEach((w) => toast.warn(w));
      toast.success(`Invoice ${sent.invoice_number} generated.`);
      const patientRef = patient.display_id || patient.patient_id;
      navigate(`/patients/${patientRef}?tab=billing`);
    } catch (err) {
      toast.error(err instanceof InvoiceApiError ? err.message : "Failed to generate bill.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    if (!invoiceId) { toast.error("Select a patient and visit first."); return; }
    toast.success("Draft saved.");
    const patientRef = patient?.display_id || patient?.patient_id;
    navigate(patientRef ? `/patients/${patientRef}?tab=billing` : "/billing");
  };

  // ── Bill Summary (server values when available, local fallback) ──────────────
  const ss = serverSummary;
  const summarySubtotalPaise = ss
    ? (ss.consultation_fee_paise ?? 0) + (ss.procedure_charges_paise ?? 0) + (ss.medication_charges_paise ?? 0)
    : Math.round(subtotal * 100);
  const summaryDiscountPaise = ss?.discount_paise ?? Math.round(discountAmt * 100);
  const summaryTotalPaise    = Math.max(0, summarySubtotalPaise - summaryDiscountPaise);
  const summaryTotalDisplay  = ss?.total_display ?? formatINR(summaryTotalPaise);

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-[1300px] mx-auto px-4 py-6">
          <div className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[20px] shadow-[0px_10px_15px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col md:flex-row min-h-[calc(100vh-7rem)]">

            {/* Billing sub-navigation */}
            <BillingSidebar />

            {/* Main content */}
            <div className="flex-1 px-4 sm:px-8 py-6 sm:py-8 overflow-y-auto">
              <div className="flex flex-col gap-6 max-w-[920px]">

                {/* Back to patient */}
                {urlPatientId && (
                  <button
                    onClick={() => navigate(`/patients/${patient?.display_id ?? urlPatientId}?tab=billing`)}
                    className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors w-fit"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                    Back to Patient
                  </button>
                )}

                {/* ── Patient Details ─────────────────────────────────────────── */}
                <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                  <h2 className="text-base font-semibold text-[#0f172b] mb-5 flex items-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b8db" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Patient Details
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Search Patient</label>
                      <PatientSearchDropdown
                        query={patientQuery}
                        onQueryChange={setPatientQuery}
                        onSelect={handleSelectPatient}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Patient ID</label>
                      <ROInput value={patient?.display_id ?? ""} placeholder="PT-2025-0001" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Patient Name</label>
                      <ROInput value={patient ? (patient.name || `${patient.first_name} ${patient.last_name}`) : ""} placeholder="Select a patient" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Phone</label>
                      <ROInput value={patient?.contact_number ?? ""} placeholder="—" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Doctor Name</label>
                      <ROInput value={doctorName || (selectedVisit ? "—" : "Select a visit")} placeholder="Doctor Name" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Bill Date</label>
                      <div className="relative">
                        <DateInput
                          value={billDate}
                          onChange={(e) => setBillDate(e.target.value)}
                          className="w-full pl-9 pr-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        </span>
                      </div>
                      {billDate && <p className="mt-1 text-xs text-slate-500">{formatDateDisplay(billDate)}</p>}
                    </div>
                  </div>

                  {/* Visit selector */}
                  <div className="mt-4">
                    <label className="block text-xs font-medium text-[#0a0a0a] mb-1">
                      Visit
                      <span className="ml-1 text-slate-400 font-normal">(required — invoice is linked to the visit)</span>
                    </label>
                    {!patient ? (
                      <div className="w-full px-3 py-2 text-sm text-slate-400 bg-slate-50 border border-[#cefafe] rounded-lg cursor-not-allowed">
                        Select a patient first
                      </div>
                    ) : visitsLoading ? (
                      <div className="w-full px-3 py-2 text-sm text-slate-400 bg-white/50 border border-[#cefafe] rounded-lg flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-[#00b8db]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        Loading visits…
                      </div>
                    ) : visits.length === 0 ? (
                      <div className="w-full px-3 py-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg">
                        No visits found for this patient — create a visit first.
                      </div>
                    ) : (
                      <div className="relative">
                        <select
                          value={selectedVisit?.visit_id ?? ""}
                          onChange={(e) => {
                            const v = visits.find((x) => x.visit_id === e.target.value) ?? null;
                            setSelectedVisit(v);
                          }}
                          className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 appearance-none pr-8"
                        >
                          <option value="">Select a visit…</option>
                          {visits.map((v) => {
                            const doc = doctors.find((d) => d.doctor_id === v.doctor_id);
                            return (
                              <option key={v.visit_id} value={v.visit_id}>
                                {formatVisitLabel(v, doc?.name)}
                              </option>
                            );
                          })}
                        </select>
                        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                        </span>
                      </div>
                    )}
                  </div>
                </section>

                {/* ── Bill Items ──────────────────────────────────────────────── */}
                <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                  <h2 className="text-base font-semibold text-[#0f172b] mb-4 flex items-center gap-2">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b8db" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    Bill Items
                  </h2>

                  {/* Mobile: card per item — full-width inputs so values are always readable */}
                  <div className="sm:hidden space-y-3">
                    {items.map((item) => {
                      const lineAmt = item.quantity * item.rate * (1 - item.discount / 100);
                      return (
                        <div key={item.id} className="rounded-xl border border-[#cefafe] bg-white/60 p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateItem(item.id, "description", e.target.value)}
                              placeholder="Service or item description"
                              className="flex-1 px-3 py-2 text-base sm:text-sm bg-white border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 placeholder-[#717182]"
                            />
                            <button
                              onClick={() => removeItem(item.id)}
                              disabled={items.length === 1}
                              className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-0 disabled:pointer-events-none shrink-0"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-slate-500 mb-1">Category</label>
                            <select
                              value={item.category || "procedure"}
                              onChange={(e) => updateItem(item.id, "category", e.target.value)}
                              className="w-full px-3 py-2 text-base sm:text-sm bg-white border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 text-slate-700 capitalize"
                            >
                              <option value="consultation">Consultation</option>
                              <option value="medication">Medication</option>
                              <option value="procedure">Procedure</option>
                            </select>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 mb-1">Qty</label>
                              <input
                                type="number" min={1}
                                value={item.quantity}
                                onChange={(e) => updateItem(item.id, "quantity", e.target.value)}
                                className="w-full px-3 py-2 text-base sm:text-sm bg-white border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 text-center"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 mb-1">Rate (₹)</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={item.rate === 0 ? "" : item.rate}
                                onChange={(e) => updateItem(item.id, "rate", e.target.value)}
                                placeholder="0"
                                className="w-full px-3 py-2 text-base sm:text-sm bg-white border border-[#00b8db] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 font-medium"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-medium text-slate-500 mb-1">Disc (%)</label>
                              <input
                                type="number" min={0} max={100}
                                value={item.discount === 0 ? "" : item.discount}
                                onChange={(e) => updateItem(item.id, "discount", e.target.value)}
                                placeholder="0"
                                className="w-full px-3 py-2 text-base sm:text-sm bg-white border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                              />
                            </div>
                          </div>
                          <div className="flex items-center justify-end">
                            <div className="text-right">
                              <span className="text-[10px] font-medium text-slate-500 block">Amount</span>
                              <span className="text-sm font-semibold text-[#0a0a0a]">
                                {lineAmt > 0 ? `₹${new Intl.NumberFormat("en-IN").format(Math.round(lineAmt))}` : "—"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: scrollable table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm" style={{ minWidth: 640 }}>
                      <thead>
                        <tr className="text-left border-b border-slate-100">
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm min-w-[200px]">Description</th>
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm w-36">Category</th>
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm w-24 text-center">Quantity</th>
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm w-32">Rate (₹)</th>
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm w-24">Discount</th>
                          <th className="pb-2 pr-3 font-medium text-[#0a0a0a] text-sm w-28 text-right">Amount (₹)</th>
                          <th className="pb-2 w-8" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {items.map((item) => {
                          const lineAmt = item.quantity * item.rate * (1 - item.discount / 100);
                          return (
                            <tr key={item.id} className="group">
                              <td className="py-2 pr-3">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateItem(item.id, "description", e.target.value)}
                                  placeholder="Service or item description"
                                  className="w-full px-3 py-1.5 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 placeholder-[#717182]"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <select
                                  value={item.category || "procedure"}
                                  onChange={(e) => updateItem(item.id, "category", e.target.value)}
                                  className="w-full px-2.5 py-1.5 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 text-slate-700 capitalize"
                                >
                                  <option value="consultation">Consultation</option>
                                  <option value="medication">Medication</option>
                                  <option value="procedure">Procedure</option>
                                </select>
                              </td>
                              <td className="py-2 pr-3">
                                <input
                                  type="number"
                                  min={1}
                                  value={item.quantity}
                                  onChange={(e) => updateItem(item.id, "quantity", e.target.value)}
                                  className="w-full px-3 py-1.5 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 text-center"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={item.rate === 0 ? "" : item.rate}
                                  onChange={(e) => updateItem(item.id, "rate", e.target.value)}
                                  placeholder="0"
                                  className="w-full px-3 py-1.5 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={item.discount === 0 ? "" : item.discount}
                                  onChange={(e) => updateItem(item.id, "discount", e.target.value)}
                                  placeholder="0%"
                                  className="w-full px-3 py-1.5 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30"
                                />
                              </td>
                              <td className="py-2 pr-3">
                                <div className="w-full px-3 py-1.5 text-sm bg-[#f1f5f9] border border-[#cefafe] rounded-lg text-right font-medium text-[#0a0a0a] opacity-50 cursor-not-allowed select-none">
                                  {lineAmt > 0 ? new Intl.NumberFormat("en-IN").format(Math.round(lineAmt)) : "—"}
                                </div>
                              </td>
                              <td className="py-2">
                                <button
                                  onClick={() => removeItem(item.id)}
                                  disabled={items.length === 1}
                                  className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-0 disabled:pointer-events-none"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <button
                    onClick={() => setItems((prev) => [...prev, newItem()])}
                    className="mt-3 flex items-center gap-2 px-3 py-1.5 text-sm text-[#007595] border border-[#53eafd] bg-white rounded-lg hover:bg-[#f0fdfe] transition"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Item
                  </button>
                </section>

                {/* ── Additional Details + Bill Summary ──────────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4">

                  {/* Additional Details */}
                  <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                    <h2 className="text-base font-semibold text-[#0f172b] mb-5">Additional Details</h2>
                    <div className="flex flex-col gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Notes</label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Add any additional notes or comments..."
                          rows={3}
                          maxLength={500}
                          className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30 placeholder-[#717182] resize-none"
                        />
                      </div>
                    </div>
                  </section>

                  {/* Bill Summary */}
                  <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6 flex flex-col gap-3">
                    <h2 className="text-base font-semibold text-[#0f172b]">Bill Summary</h2>

                    {isPatching && (
                      <div className="flex items-center gap-2 text-xs text-[#007595]">
                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        Calculating…
                      </div>
                    )}

                    <div className="flex justify-between text-sm">
                      <span className="text-[#45556c]">Subtotal</span>
                      <span className="text-[#0f172b] font-medium">{formatINR(summarySubtotalPaise)}</span>
                    </div>

                    {/* Editable discount % + rupee display */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[#45556c]">Discount</span>
                      <div className="flex items-center gap-2">
                        {summaryDiscountPaise > 0 && (
                          <span className="text-emerald-600 text-xs font-medium">
                            -{formatINR(summaryDiscountPaise)}
                          </span>
                        )}
                        <div className="relative w-16">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={discountPct === 0 ? "" : discountPct}
                            onChange={(e) => setDiscountPct(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                            placeholder="0"
                            className="w-full px-2 py-1 text-base sm:text-sm text-right bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00b8db]/30 pr-5"
                          />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">%</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-sm">
                      <label htmlFor="bill-gst-rate" className="text-[#45556c]">GST</label>
                      <div className="flex items-center gap-2">
                        {(ss?.gst_amount_paise ?? 0) > 0 && (
                          <span className="text-[#0f172b] text-xs font-medium">
                            +{formatINR(ss?.gst_amount_paise ?? 0)}
                          </span>
                        )}
                        <select
                          id="bill-gst-rate"
                          value={ss?.gst_rate ?? 0}
                          onChange={(e) => handleGstChange(Number(e.target.value) as GstSlab)}
                          disabled={!invoiceId || isPatching}
                          className="w-16 px-2 py-1 text-base sm:text-sm text-right bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#00b8db]/30 disabled:opacity-50"
                        >
                          {GST_SLABS.map((slab) => (
                            <option key={slab} value={slab}>{slab}%</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="border-t border-[#a2f4fd] pt-3 flex justify-between items-baseline">
                      <span className="text-base font-semibold text-[#0f172b]">Total Amount</span>
                      <span className="text-lg font-bold text-[#007595]">{summaryTotalDisplay}</span>
                    </div>

                    {/* CTAs */}
                    <div className="flex flex-col gap-2 mt-1">
                      <button
                        onClick={handleGenerateBill}
                        disabled={isSubmitting || !invoiceId}
                        className="w-full py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] hover:opacity-90 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? "Generating…" : "Generate Bill"}
                      </button>
                      <button
                        onClick={handleSaveDraft}
                        disabled={isSubmitting || !invoiceId}
                        className="w-full py-2 rounded-lg text-sm font-medium text-[#007595] border border-[#53eafd] bg-white hover:bg-[#f0fdfe] transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Save as Draft
                      </button>
                    </div>
                  </section>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBillPage;
